/**
 * AgentInsightBubble — Consolidated agent insight bubble (GREEN + BLUE mode)
 *
 * DEC-052: Single debug_event channel — all data comes from debugEvents[].
 * Replaces per-event SystemBubble with ONE bubble per user message.
 * Shows summary line + expandable per-agent cards.
 */

import { useState, useCallback } from 'react'
import { clsx } from 'clsx'
import type { DebugEvent, DebugAgentType } from '../../stores/chatStore'
import { DEBUG_AGENT_ICONS } from '../../stores/chatStore'

interface AgentInsightBubbleProps {
  debugEvents: DebugEvent[]
}

// Agent-specific background colors
const agentColors: Record<DebugAgentType, string> = {
  ContentAnalyzer: 'bg-blue-50 border-blue-200',
  ContextObserver: 'bg-purple-50 border-purple-200',
  CrisisDetector: 'bg-red-50 border-red-300',
  VoiceAnalysis: 'bg-green-50 border-green-200',
  ImageAnalysis: 'bg-pink-50 border-pink-200',
  PharmaAdvisor: 'bg-orange-50 border-orange-200',
  Consilium: 'bg-indigo-50 border-indigo-200',
  DossierUpdater: 'bg-gray-50 border-gray-200',
  RAGRetriever: 'bg-cyan-50 border-cyan-200',
  KnowledgeBase: 'bg-amber-50 border-amber-200',
  SessionSummarizer: 'bg-teal-50 border-teal-200',
  Orchestrator: 'bg-yellow-50 border-yellow-200',
  Performance: 'bg-emerald-50 border-emerald-200',
  ResponseFlow: 'bg-sky-50 border-sky-200',
  TherapistContext: 'bg-violet-50 border-violet-200',
}

// Build summary line from debug events
function buildSummary(events: DebugEvent[]): string {
  const parts: string[] = []

  // Emotion from ContentAnalyzer
  const ca = events.find(e => e.agent === 'ContentAnalyzer')
  if (ca?.data.emotion) parts.push(String(ca.data.emotion))

  // Crisis level
  if (ca?.data.crisis && ca.data.crisis !== 'none') parts.push(`crisis: ${ca.data.crisis}`)

  // Mismatch from VoiceAnalysis
  const va = events.find(e => e.agent === 'VoiceAnalysis')
  if (va) {
    const mismatchCount = va.data.mismatch_count as number | undefined
    const multimodal = va.data.multimodal as string | undefined
    if ((mismatchCount && mismatchCount > 0) || (multimodal && multimodal.includes('MISMATCH'))) {
      parts.push(`⚠️ MISMATCH${mismatchCount ? ` (${mismatchCount})` : ''}`)
    }
  }

  // Routing
  const orch = events.find(e => e.agent === 'Orchestrator')
  if (orch?.data.therapist) {
    parts.push(`→ ${orch.data.therapist}`)
  }

  return parts.join(' | ')
}

// Format data value for display
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Build enriched display entries for ContentAnalyzer from enriched debug_event data (DEC-052)
function buildContentAnalyzerEntries(data: Record<string, unknown>): Array<{ label: string; value: string }> {
  const entries: Array<{ label: string; value: string }> = []

  // Emotion detail (enriched data from DEC-052)
  const ed = data.emotion_detail as Record<string, unknown> | undefined
  if (ed) {
    entries.push({ label: 'Emotion', value: `${ed.primary} (${Math.round(Number(ed.intensity) * 100)}%, valence: ${Number(ed.valence).toFixed(1)})` })
    if (ed.secondary) entries.push({ label: 'Secondary', value: String(ed.secondary) })
  } else if (data.emotion) {
    entries.push({ label: 'Emotion', value: String(data.emotion) })
  }

  // Crisis detail
  const cd = data.crisis_detail as Record<string, unknown> | undefined
  if (cd) {
    entries.push({ label: 'Crisis', value: String(data.crisis) })
    const signals = cd.signals as string[] | undefined
    if (signals && signals.length > 0) entries.push({ label: 'Signals', value: signals.join(', ') })
  } else if (data.crisis) {
    entries.push({ label: 'Crisis', value: String(data.crisis) })
  }

  // Medication
  if (data.medication) {
    entries.push({ label: 'Medication', value: Array.isArray(data.medication) ? data.medication.join(', ') : String(data.medication) })
  }

  // Learning
  const learning = data.learning as Record<string, unknown> | undefined
  if (learning) {
    entries.push({ label: 'Learning', value: `${learning.type}: ${learning.content}` })
  }

  // Handoff
  const handoff = data.handoff as Record<string, unknown> | undefined
  if (handoff) {
    entries.push({ label: 'Handoff', value: `→ ${handoff.target} (${handoff.urgency})` })
  }

  // Media
  if (data.media) {
    entries.push({ label: 'Media', value: String(data.media) })
  }

  return entries
}

export default function AgentInsightBubble({ debugEvents }: AgentInsightBubbleProps) {
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState(false)

  const copyDebugContext = useCallback(() => {
    const lines = debugEvents.map(event => {
      const icon = DEBUG_AGENT_ICONS[event.agent] || event.icon || '⚙️'
      return `### ${icon} ${event.agent}\n\`\`\`json\n${JSON.stringify(event.data, null, 2)}\n\`\`\``
    })
    const text = `## Debug Context (msg #${debugEvents[0]?.messageId})\n\n${lines.join('\n\n')}`
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }, [debugEvents])

  if (debugEvents.length === 0) return null

  const summary = buildSummary(debugEvents)

  const toggleAgent = (agent: string) => {
    setExpandedAgents(prev => {
      const next = new Set(prev)
      if (next.has(agent)) next.delete(agent)
      else next.add(agent)
      return next
    })
  }

  return (
    <div className="mx-auto max-w-md my-2 rounded-lg border border-neutral-200 bg-neutral-50 text-xs font-mono overflow-hidden">
      {/* Summary header — always visible */}
      <div className="px-3 py-1.5 text-neutral-500 border-b border-neutral-200 text-[10px] flex items-center gap-2">
        {summary && <span className="flex-1">{summary}</span>}
        {!summary && <span className="flex-1" />}
        <button
          onClick={(e) => { e.stopPropagation(); copyDebugContext() }}
          className="text-neutral-400 hover:text-neutral-600 transition-colors shrink-0"
          title="Copy debug context as markdown"
        >
          {copied ? '✓' : '📋'}
        </button>
      </div>

      {/* Agent cards */}
      <div className="divide-y divide-neutral-100">
        {debugEvents.map((event) => {
          const icon = DEBUG_AGENT_ICONS[event.agent] || event.icon || '⚙️'
          const isExpanded = expandedAgents.has(event.agent)
          const colorClass = agentColors[event.agent] || 'bg-neutral-50 border-neutral-200'

          const isContentAnalyzer = event.agent === 'ContentAnalyzer'
          const caEntries = isContentAnalyzer ? buildContentAnalyzerEntries(event.data) : []
          const entries = Object.entries(event.data)

          // Build preview for collapsed state
          const preview = entries.length > 0
            ? `${Object.keys(event.data)[0]}: ${formatValue(Object.values(event.data)[0])}`
            : ''

          return (
            <div
              key={event.id}
              className={clsx('cursor-pointer transition-colors hover:bg-neutral-100', isExpanded && colorClass)}
              onClick={() => toggleAgent(event.agent)}
            >
              {/* Agent header */}
              <div className="flex items-center gap-2 px-3 py-1.5 text-neutral-600">
                <span>{icon}</span>
                <span className="font-semibold">{event.agent}</span>
                {!isExpanded && preview && (
                  <span className="text-neutral-400 truncate flex-1 text-[10px]">{preview}</span>
                )}
                <span className="ml-auto text-[9px] text-neutral-400">{isExpanded ? '▼' : '▶'}</span>
              </div>

              {/* Expanded data */}
              {isExpanded && (
                <div className="px-3 pb-2 space-y-0.5">
                  {/* ContentAnalyzer: enriched structured entries */}
                  {isContentAnalyzer && caEntries.length > 0 ? (
                    caEntries.map((entry, idx) => (
                      <div key={idx} className="flex gap-2 text-neutral-700">
                        <span className="text-neutral-500 shrink-0">{entry.label}:</span>
                        <span className="break-all">{entry.value}</span>
                      </div>
                    ))
                  ) : (
                    /* Other agents: raw key-value pairs */
                    entries.map(([key, value]) => (
                      <div key={key} className="flex gap-2 text-neutral-700">
                        <span className="text-neutral-500 shrink-0">{key}:</span>
                        <span className="break-all">{formatValue(value)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
